/**
 * src/pages/Profile.js
 *
 * Public profile page displaying user's products and wishlist.
 * Supports real-time updates, privacy controls, and comprehensive profile management.
 * 
 * Features:
 * - Real-time product/wishlist updates via Firestore listeners
 * - Privacy-aware content display
 * - Report and share functionality
 * - Owner-specific edit and delete actions
 * - Responsive grid layouts for different device sizes
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, User, Plus, Package, X, Star, HelpCircle, Settings, Shield, ChevronRight } from "lucide-react";
import useFarmWishlist from "../hooks/useFarmWishlist";
import ProfileProductCard from "../components/farm/ProfileProductCard";
import ProfileHeader from "../components/view-profile/ProfileHeader";
import ProfileActions from "../components/view-profile/ProfileActions";
import ProfileTabs from "../components/view-profile/ProfileTabs";
import EmptyState from "../components/view-profile/EmptyState";
import ProfilePageShell from "../components/view-profile/ProfilePageShell";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { useProfile } from "../hooks/useProfile";
import ProfileEditForm from "../components/profile/ProfileEditForm";
import {
  PROFILE_DISPLAY,
  PROFILE_TABS,
  PROFILE_LOADING_STATES,
  SHARE_CONFIG,
} from "../constants/profileConstants";
import { submitEntityReport, REPORT_ENTITY_TYPES } from "../services/reportsService";
import { wishlistRepository } from "../repositories/wishlistRepository";
import { productRepository } from "../repositories/productRepository";
import { ratingsRepository } from "../repositories/ratingsRepository";
import ReportEntityDialog from "../components/reports/ReportEntityDialog";
import { errorDev } from "../utils/logger.js";
import { useAdmin } from "../hooks/useAdmin";

const MAX_WISH_COUNT_LISTENERS = 20;

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  // Use profile and common namespaces with proper fallback chain
  const { t, i18n, ready: i18nReady } = useTranslation(["profile", "common"]);
  
  // Safe translation with fallback - prevents broken UI from missing keys
  const safeT = useMemo(() => {
    return (key, fallback = key) => {
      if (!i18nReady) return fallback;
      const result = t(key);
      // If key not found, i18next returns the key itself
      return result === key ? fallback : result;
    };
  }, [t, i18nReady]);

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [activeTab, setActiveTab] = useState(PROFILE_TABS.PRODUCTS);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [showAllWishlist, setShowAllWishlist] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [reportDialog, setReportDialog] = useState({ open: false, entityType: null, entityId: null, label: "" });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editInitialSection, setEditInitialSection] = useState("all");
  const editModalRef = useRef(null);
  const lastFocusedRef = useRef(null);
  
  // Real-time state for critical data
  const [realTimeProducts, setRealTimeProducts] = useState([]);
  const [realTimeWishlist, setRealTimeWishlist] = useState([]);
  const [loadingRealTime, setLoadingRealTime] = useState({
    [PROFILE_LOADING_STATES.PRODUCTS]: true,
    [PROFILE_LOADING_STATES.WISHLIST]: true
  });

  const {
    profile,
     products: hookProducts,
     wishlist: hookWishlist,
    ratingStats,
    loading,
    currentUser,
    signedIn,
    isOwner,
    deleteProduct,
    updateProfile,
  } = useProfile(userId);

  const { isWished, toggleWishlist: rawToggleWishlist } = useFarmWishlist();
  const { isAdmin, isSuperAdmin } = useAdmin();
  const [wishCounts, setWishCounts] = useState({});
  const wishCountUnsubs = useRef({});

  const toMillis = useCallback((value) => {
    if (value?.toMillis) return value.toMillis();
    const parsed = new Date(value || 0).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const sortByCreatedAtDesc = useCallback((rows) => {
    return [...rows].sort((a, b) => toMillis(b?.createdAt) - toMillis(a?.createdAt));
  }, [toMillis]);

  const mergeById = useCallback((primary = [], fallback = []) => {
    const merged = new Map();
    (fallback || []).forEach((item) => {
      if (item?.id) merged.set(item.id, item);
    });
    (primary || []).forEach((item) => {
      if (item?.id) merged.set(item.id, item);
    });
    return Array.from(merged.values());
  }, []);

  const isProductOwner = useCallback((product) => {
    const ownerId = product?.poster?.userId || product?.userId || product?.ownerId || product?.sellerId || null;
    return Boolean(currentUser?.uid && ownerId && currentUser.uid === ownerId);
  }, [currentUser?.uid]);

  // Set up real-time listeners for products and wishlist
  useEffect(() => {
    if (!userId) return;

try {
      // Real-time products listener. The four ownership fields are queried in
      // parallel and merged by id inside productRepository so this page shares
      // one ownership query definition with useProfile.
      const productUnsubscribers = [];
      let productsSettled = false;
      const productListener = productRepository.subscribeByOwner(
        userId,
        (rows) => {
          setRealTimeProducts(rows);
          if (!productsSettled) {
            productsSettled = true;
            setLoadingRealTime((prev) => ({ ...prev, [PROFILE_LOADING_STATES.PRODUCTS]: false }));
          }
          setError(null);
        },
        (error) => {
          errorDev("Profile: product listener failed", error);
          if (!productsSettled) {
            productsSettled = true;
            setLoadingRealTime((prev) => ({ ...prev, [PROFILE_LOADING_STATES.PRODUCTS]: false }));
          }
        }
      );
      if (typeof productListener === "function") productUnsubscribers.push(productListener);

      // Real-time wishlist listener, routed through wishlistRepository so this
      // page shares one wishlist query definition with useProfile.
       const wishlistUnsubscribe = wishlistRepository.subscribeAll(
         userId,
         async (wishlistData) => {
           // Enhance wishlist items with product data if needed
           const enhancedWishlist = await Promise.all(
             wishlistData.map(async (item) => {
               if (item.productId && !item.productTitle) {
                 try {
                   const product = await productRepository.getById(item.productId);

                   if (product) {
                     return {
                       ...item,
                       productTitle: product.title,
                       productImage: product.images?.[0],
                       price: product.price,
                       currency: product.currency,
                       sellerId: product.poster?.userId,
                       sellerName: product.poster?.username || product.poster?.name
                     };
                   }
                 } catch (error) {
                   errorDev("Profile: wishlist product enrichment failed", error);
                 }
               }
               return item;
             })
           );

           setRealTimeWishlist(enhancedWishlist);
           setLoadingRealTime(prev => ({ ...prev, [PROFILE_LOADING_STATES.WISHLIST]: false }));
           setError(null);
         },
         (error) => {
           errorDev("Profile: wishlist listener failed", error);
           setLoadingRealTime(prev => ({ ...prev, [PROFILE_LOADING_STATES.WISHLIST]: false }));
         }
       );

      // Load reviews
      loadReviews();

      // Cleanup listeners
      return () => {
        productUnsubscribers.forEach((unsubscribe) => {
          if (typeof unsubscribe === "function") unsubscribe();
        });
        if (typeof wishlistUnsubscribe === "function") wishlistUnsubscribe();
      };
    } catch (err) {
      setError(safeT("profile.messages.loadFailed", "We could not load this profile right now. Please try again."));
    }
  }, [sortByCreatedAtDesc, userId]);

  // Use real-time data when available, fallback to hook data
  const products = useMemo(
    () => sortByCreatedAtDesc(mergeById(realTimeProducts, hookProducts)),
    [hookProducts, mergeById, realTimeProducts, sortByCreatedAtDesc]
  );
  const wishlist = useMemo(
    () => sortByCreatedAtDesc(mergeById(realTimeWishlist, hookWishlist)),
    [hookWishlist, mergeById, realTimeWishlist, sortByCreatedAtDesc]
  );

  // Combine loading states
  const combinedLoading = {
    [PROFILE_LOADING_STATES.PROFILE]: loading?.profile,
     [PROFILE_LOADING_STATES.PRODUCTS]: loading?.products || loadingRealTime[PROFILE_LOADING_STATES.PRODUCTS],
     [PROFILE_LOADING_STATES.WISHLIST]: loading?.wishlist || loadingRealTime[PROFILE_LOADING_STATES.WISHLIST]
  };

  const loadReviews = async () => {
    setLoadingReviews(true);
    try {
      // Single canonical ratings read: ratingsRepository merges the canonical
      // `targetId` field with the legacy `toUserId` alias.
      const reviewsData = await ratingsRepository.listForTarget(userId);
      setReviews(reviewsData);
    } catch (err) {
      errorDev("Profile: failed to load reviews", err);
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  // Normalize wishlist items for ProductCard (same as your old logic)
  const normalizeWishlistItem = (item) => {
    if (!item) return null;
    
    // If it's already a product doc with `id`, return as-is
    if (item.id && item.title) return item;
    
    // If it's a wishlist doc with productId, synthesize a light product object
    if (item.productId) {
      return {
        id: item.productId,
        title: item.productTitle || item.title || safeT("content.savedItem", "Saved item"),
        name: item.productTitle || item.title || safeT("content.savedItem", "Saved item"),
        images: item.productImage ? [item.productImage] : item.images || [],
        price: item.price ?? null,
        currency: item.currency || "RWF",
        poster: item.sellerId ? { 
          userId: item.sellerId, 
          name: item.sellerName || null 
        } : item.poster || {},
        // Keep original wishlist doc attached
        __wishlistDoc: { ...item },
      };
    }
    
    // Fallback: if it looks like an entry with nested product
    if (item.product) {
      return { id: item.product.id || item.productId, ...item.product };
    }
    
    return null;
  };

  const normalizedWishlist = Array.isArray(wishlist)
    ? wishlist.map(normalizeWishlistItem).filter(Boolean)
    : [];

  const allCardProductIds = useMemo(() => {
    const ids = new Set();
    products.forEach((item) => {
      if (item?.id) ids.add(item.id);
    });
    normalizedWishlist.forEach((item) => {
      if (item?.id) ids.add(item.id);
    });
    return Array.from(ids);
  }, [products, normalizedWishlist]);

  const productRecordsById = useMemo(() => {
    const records = new Map();
    [...products, ...normalizedWishlist].forEach((item) => {
      if (item?.id) records.set(item.id, item);
    });
    return records;
  }, [normalizedWishlist, products]);

  const wishCountListenerIds = useMemo(
    () => Array.from(allCardProductIds)
      .filter((productId) => productRecordsById.get(productId)?.wishCount == null)
      .slice(0, MAX_WISH_COUNT_LISTENERS),
    [allCardProductIds, productRecordsById]
  );

  useEffect(() => {
    const currentIds = new Set(wishCountListenerIds);

    Object.keys(wishCountUnsubs.current).forEach((id) => {
      if (!currentIds.has(id)) {
        wishCountUnsubs.current[id]();
        delete wishCountUnsubs.current[id];
      }
    });

    wishCountListenerIds.forEach((productId) => {
      if (wishCountUnsubs.current[productId]) return;
      // The wish count lives on the product document; the repository owns the
      // ref so this page does not build a Firestore path itself.
      wishCountUnsubs.current[productId] = productRepository.subscribeById(
        productId,
        (row) => {
          if (row && row.wishCount != null) {
            setWishCounts((prev) => ({ ...prev, [productId]: row.wishCount }));
          }
        },
        () => {}
      );
    });

    return () => {
      Object.values(wishCountUnsubs.current).forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (err) {
          // ignore listener cleanup errors
          errorDev("Operation failed:", err);
        }
      });
      wishCountUnsubs.current = {};
    };
  }, [wishCountListenerIds]);

  const handleToggleWishlist = useCallback(async (product) => {
    if (!currentUser?.uid) {
      navigate("/login");
      return;
    }

    try {
      const nowWished = await rawToggleWishlist(product);
      // The wish count is best-effort and must not block the UI; the
      // repository owns the write so the page does not touch Firestore.
      await productRepository.incrementWishCount(product.id, nowWished ? 1 : -1);
    } catch (err) {
      // ignore toggle errors to preserve page flow
      errorDev("Operation failed:", err);
    }
  }, [currentUser?.uid, navigate, rawToggleWishlist]);

  const handleToggleProductHidden = useCallback(async (product) => {
    if (!isProductOwner(product)) return;
    try {
      const nextHidden = !(product?.hidden || product?.isHidden || product?.isPrivate);
      await productRepository.updateVisibility(product.id, nextHidden);
    } catch (err) {
      setError(err?.message || safeT("profile.edit.messages.updateFailed", "Failed to update profile. Please try again."));
    }
  }, [isProductOwner, safeT]);

  const handleShare = async () => {
    const url = `${window.location.origin}/profile/${userId}`;
    const profileName = profile?.name || SHARE_CONFIG.FALLBACK_NAME;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: safeT("messages.shareTitle", "Check out {{name}}'s profile", { name: profileName }),
          text: safeT("messages.shareText", "View {{name}}'s profile on Orrith - Cycle of Certainty", { name: profileName }),
          url
        });
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(safeT("profile.messages.shareFailed", "Unable to share profile right now."));
        }
      }
    }

    // Fallback to clipboard copy
    try {
      await navigator.clipboard.writeText(url);
      // Success - UI feedback handled by component state
    } catch (err) {
      // Final fallback to prompt
      errorDev("Operation failed:", err);
      prompt(safeT("profile.messages.shareLinkPrompt", "Copy this link to share the profile:"), url);
    }
  };

  const openReportDialog = useCallback((payload) => {
    if (!currentUser?.uid) {
      navigate("/login");
      return;
    }
    setReportDialog({
      open: true,
      entityType: payload.entityType,
      entityId: payload.entityId,
      label: payload.label,
    });
  }, [currentUser?.uid, navigate]);

  const handleReport = () => {
    openReportDialog({
      entityType: REPORT_ENTITY_TYPES.PROFILE,
      entityId: userId,
      label: "profile",
    });
  };

  const submitReport = useCallback(async ({ reason, details }) => {
    if (!reportDialog.entityType || !reportDialog.entityId) return;
    setSubmittingReport(true);
    try {
      await submitEntityReport(currentUser, {
        entityType: reportDialog.entityType,
        entityId: reportDialog.entityId,
        reason,
        details,
        entityOwnerId: userId,
        metadata: {
          source: "profile_page",
          profileId: userId,
          profileUsername: profile?.username || "",
        },
      });
      setError(null);
      setReportDialog({ open: false, entityType: null, entityId: null, label: "" });
    } catch (err) {
      setError(err?.message || "Failed to submit report");
    } finally {
      setSubmittingReport(false);
    }
  }, [profile?.username, reportDialog.entityId, reportDialog.entityType, userId]);

  const handleOpenDeleteDialog = (product) => {
    if (!isProductOwner(product)) return;
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete || !isProductOwner(productToDelete)) return;
    setDeletingProduct(true);
    try {
      await deleteProduct(productToDelete.id);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    } catch (err) {
      setError(safeT("profile.actions.deleteFailed", "We could not delete this product right now. Please try again."));
    } finally {
      setDeletingProduct(false);
    }
  };

  const handleEditProfile = () => {
    lastFocusedRef.current = document.activeElement;
    setEditInitialSection("all");
    setEditModalOpen(true);
  };

  const handleEditPhoto = () => {
    lastFocusedRef.current = document.activeElement;
    setEditInitialSection("photo");
    setEditModalOpen(true);
  };

  const closeEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditInitialSection("all");
    if (lastFocusedRef.current && typeof lastFocusedRef.current.focus === "function") {
      lastFocusedRef.current.focus();
    }
  }, []);

  const handleViewReviews = () => {
    navigate(`/profile/${userId}/reviews`);
  };

  const handleEditSubmitSuccess = () => {
    closeEditModal();
  };

  useEffect(() => {
    if (!editModalOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const modalEl = editModalRef.current;
    const focusableSelector = [
      "a[href]",
      "button:not([disabled])",
      "textarea:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");

    const focusables = modalEl ? Array.from(modalEl.querySelectorAll(focusableSelector)) : [];
    const firstFocusable = focusables[0];
    if (firstFocusable && typeof firstFocusable.focus === "function") {
      firstFocusable.focus();
    }

    const handleModalKeydown = (event) => {
      if (!editModalOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        closeEditModal();
        return;
      }

      if (event.key !== "Tab") return;
      const currentFocusables = modalEl ? Array.from(modalEl.querySelectorAll(focusableSelector)) : [];
      if (currentFocusables.length === 0) return;

      const first = currentFocusables[0];
      const last = currentFocusables[currentFocusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleModalKeydown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleModalKeydown);
    };
  }, [closeEditModal, editModalOpen]);

  // Get display items with "View More" logic
  const getDisplayItems = (items, showAll) => {
    if (showAll) return items;
    return items.slice(0, PROFILE_DISPLAY.ITEMS_PER_PAGE);
  };

  const isHiddenProduct = (product) => Boolean(product?.hidden || product?.isHidden || product?.isPrivate);
  const visibleProducts = products.filter((product) => !isHiddenProduct(product) || isProductOwner(product));
  const visibleWishlist = normalizedWishlist.filter((product) => !isHiddenProduct(product) || isProductOwner(product));

  const displayProducts = getDisplayItems(visibleProducts, showAllProducts);
  const displayWishlist = getDisplayItems(visibleWishlist, showAllWishlist);
  
  const hasMoreProducts = visibleProducts.length > PROFILE_DISPLAY.ITEMS_PER_PAGE;
  const hasMoreWishlist = visibleWishlist.length > PROFILE_DISPLAY.ITEMS_PER_PAGE;

  // Tab content renderer
  const renderTabContent = () => {
    switch (activeTab) {
      case PROFILE_TABS.PRODUCTS:
        return (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                {isOwner
                  ? safeT("profile.tabs.myProducts", "My Products")
                  : `${safeT("profile.tabs.products", "Products")} (${visibleProducts.length})`}
              </h2>
              {isOwner && (
                <Link
                  to="/sell-now"
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{safeT("profile.content.addProduct", "Add Product")}</span>
                  <span className="sm:hidden">{safeT("profile.content.add", "Add")}</span>
                </Link>
              )}
            </div>

            {combinedLoading[PROFILE_LOADING_STATES.PRODUCTS] ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white dark:bg-neutral-800 rounded-lg p-3 animate-pulse">
                    <div className="aspect-square bg-gray-200 dark:bg-neutral-700 rounded-lg mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded mb-1"></div>
                    <div className="h-3 bg-gray-200 dark:bg-neutral-700 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : visibleProducts.length === 0 ? (
              <EmptyState
                icon={Package}
                title={safeT("profile.content.noProducts", "No Products Yet")}
                description={isOwner 
                  ? safeT("profile.content.noProductsDesc", "Start selling by listing your first product!")
                  : safeT("profile.content.noProductsDescOther", "This user hasn't listed any products yet.")}
                action={isOwner ? {
                  label: safeT("profile.content.listFirstProduct", "List Your First Product"),
                  to: "/sell-now"
                } : null}
              />
            ) : (
              <>
                <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4">
                  {displayProducts.map((product) => {
                    const owner = isProductOwner(product);
                    return (
                      <ProfileProductCard
                        key={product.id}
                        product={product}
                        compact
                        isOwner={owner}
                        isWished={isWished(product.id)}
                        wishCount={wishCounts[product.id] ?? product.wishCount ?? 0}
                        onToggleWishlist={handleToggleWishlist}
                        onEdit={owner ? () => navigate(`/edit-product/${product.id}`) : undefined}
                        onHide={owner ? () => handleToggleProductHidden(product) : undefined}
                        onDelete={owner ? () => handleOpenDeleteDialog(product) : undefined}
                        onReport={!owner ? () => openReportDialog({
                          entityType: REPORT_ENTITY_TYPES.PRODUCT,
                          entityId: product.id,
                          label: "product",
                        }) : undefined}
                      />
                    );
                  })}
                </section>
                
                {hasMoreProducts && !showAllProducts && (
                  <div className="text-center mt-4">
                    <button
                      onClick={() => setShowAllProducts(true)}
                      className="px-4 py-2 border border-green-600 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors font-medium text-sm"
                    >
                      {safeT("profile.content.viewAll", "View All {{count}} {{type}}", { 
                        count: visibleProducts.length, 
                        type: safeT("profile.tabs.products", "Products") 
                      })}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
         );

      case PROFILE_TABS.WISHLIST:
        return (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                {safeT("profile.tabs.wishlist", "Wishlist")} ({visibleWishlist.length})
              </h2>
            </div>

            {combinedLoading[PROFILE_LOADING_STATES.WISHLIST] ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white dark:bg-neutral-800 rounded-lg p-3 animate-pulse">
                    <div className="aspect-square bg-gray-200 dark:bg-neutral-700 rounded-lg mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded mb-1"></div>
                    <div className="h-3 bg-gray-200 dark:bg-neutral-700 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : visibleWishlist.length === 0 ? (
              <EmptyState
                icon={Star}
                title={safeT("profile.content.noWishlist", "No Saved Items")}
                description={safeT("profile.content.noWishlistDesc", "Saved items will appear here.")}
              />
            ) : (
              <>
                <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4">
                  {displayWishlist.map((product) => {
                    const owner = isProductOwner(product);
                    return (
                      <ProfileProductCard
                        key={product.id}
                        product={product}
                        compact
                        isOwner={owner}
                        isWished={isWished(product.id)}
                        wishCount={wishCounts[product.id] ?? product.wishCount ?? 0}
                        onToggleWishlist={handleToggleWishlist}
                        onEdit={owner ? () => navigate(`/edit-product/${product.id}`) : undefined}
                        onHide={owner ? () => handleToggleProductHidden(product) : undefined}
                        onDelete={owner ? () => handleOpenDeleteDialog(product) : undefined}
                        onReport={!owner ? () => openReportDialog({
                          entityType: REPORT_ENTITY_TYPES.PRODUCT,
                          entityId: product.id,
                          label: "product",
                        }) : undefined}
                      />
                    );
                  })}
                </section>
                
                {hasMoreWishlist && !showAllWishlist && (
                  <div className="text-center mt-4">
                    <button
                      onClick={() => setShowAllWishlist(true)}
                      className="px-4 py-2 border border-purple-600 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors font-medium text-sm"
                    >
                      {safeT("profile.content.viewAll", "View All {{count}} {{type}}", { 
                        count: visibleWishlist.length, 
                        type: safeT("profile.tabs.wishlist", "Wishlist") 
                      })}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        );

      default:
        return null;
    }
  };

  // Declared before early-return guards so hook order never varies.
  const handleBack = () => {
    try {
      if (window.history?.length > 1) window.history.back();
      else window.location.assign("/farm");
    } catch {
      window.location.assign("/farm");
    }
  };

  // Loading state
  if (combinedLoading[PROFILE_LOADING_STATES.PROFILE]) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center p-4">
        <LoadingSpinner 
          size="large" 
          text={safeT("profile.loadingProfile", "Loading profile...")} 
        />
      </div>
    );
  }

  // Profile not found state
  if (!profile && !combinedLoading[PROFILE_LOADING_STATES.PROFILE]) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md w-full">
          <div className="w-20 h-20 bg-gray-200 dark:bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {safeT("profile.profileNotFound", "Profile Not Found")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
            {safeT("profile.profileNotFoundDesc", "The profile you're looking for doesn't exist or has been removed.")}
          </p>
          <Link 
            to="/"
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
          >
            {safeT("profile.returnHome", "Return Home")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ProfilePageShell
      title={safeT("profile.title", "Profile")}
      subtitle={profile?.username ? `@${profile.username}` : safeT("profile.subtitle", "Your public profile")}
      // Back arrow only on OTHER people's profiles — never on your own.
      onBack={!isOwner ? handleBack : undefined}
      backLabel={safeT("common:back", "Back")}
      actions={
        <div className="flex items-center gap-1">
          {isOwner && (
            <Link
              to="/settings"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
              aria-label={safeT("common:settings", "Settings")}
            >
              <Settings className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
          <Link
            to="/support"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
            aria-label={safeT("dashboard.helpCenter", "Help center")}
          >
            <HelpCircle className="h-4 w-4" aria-hidden="true" />
          </Link>
          <ProfileActions
            profile={profile}
            isOwner={isOwner}
            onEdit={handleEditProfile}
            onShare={handleShare}
            onReport={handleReport}
            signedIn={signedIn}
            compact
          />
        </div>
      }
    >
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Email Verification Badge - Show for owner if unverified */}
        {isOwner && currentUser && !currentUser.emailVerified && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
                  {safeT("auth.emailVerificationRequired", "Email Verification Required")}
                </p>
                <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                  {safeT("auth.emailVerificationMessage", "Verify your email to access all features.")}
                </p>
                <Link 
                  to="/settings"
                  className="inline-flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition"
                >
                  {safeT("auth.verifyEmail", "Verify Email")}
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Profile Header - Photo above layout */}
        <ProfileHeader
          profile={profile}
          ratingStats={ratingStats}
          reviewsCount={reviews.length}
          onViewReviews={handleViewReviews}
          isOwner={isOwner}
          onEditPhoto={handleEditPhoto}
          onMoreNew
        />

        {isAdmin && (
          <Link
            to="/admin"
            className="group mt-3 flex w-full items-center gap-3 overflow-hidden rounded-xl bg-gradient-to-r from-green-800 via-green-700 to-green-600 p-4 transition hover:from-green-700 hover:to-green-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20 text-white">
              <Shield className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold text-white">
                {isSuperAdmin
                  ? safeT("navbar.superAdminPanel", "Super Admin Panel")
                  : safeT("navbar.adminPanel", "Admin Panel")}
              </span>
              <span className="mt-0.5 block text-sm text-green-100">
                {safeT("dashboard.adminDesc", "Manage users, content, analytics & notifications.")}
              </span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-green-100 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        )}

        {/* Tabbed Navigation */}
        <div className="mt-6 mb-6">
          <ProfileTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
             productsCount={products?.length || 0}
             wishlistCount={normalizedWishlist?.length || 0}
            isOwner={isOwner}
          />
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {renderTabContent()}
        </div>

        <ConfirmDialog
          open={deleteDialogOpen}
          title={safeT("profile.actions.deleteProductTitle", "Delete product")}
          message={safeT("profile.actions.confirmDelete", "Are you sure you want to delete this product? This action cannot be undone.")}
          confirmLabel={safeT("profile.actions.delete", "Delete")}
          cancelLabel={safeT("profile.actions.cancel", "Cancel")}
          destructive
          loading={deletingProduct}
          onConfirm={handleDeleteProduct}
          onCancel={() => {
            if (deletingProduct) return;
            setDeleteDialogOpen(false);
            setProductToDelete(null);
          }}
        />

        <ReportEntityDialog
          open={reportDialog.open}
          entityLabel={reportDialog.label || "content"}
          submitting={submittingReport}
          onSubmit={submitReport}
          onClose={() => {
            if (submittingReport) return;
            setReportDialog({ open: false, entityType: null, entityId: null, label: "" });
          }}
        />

        {isOwner && editModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label={safeT("profile.edit.title", "Edit Profile")}>
            <button
              type="button"
              aria-label={safeT("common:close", "Close")}
              className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
              onClick={closeEditModal}
            />

            <div ref={editModalRef} className="relative z-10 w-full sm:max-w-3xl h-[92vh] sm:h-auto sm:max-h-[90vh] bg-gray-50 dark:bg-neutral-900 sm:rounded-2xl shadow-2xl border-0 sm:border sm:border-gray-200 dark:sm:border-neutral-800 overflow-hidden flex flex-col">
              <div className="sticky top-0 z-10 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-3 sm:px-4 h-14 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300"
                    aria-label={safeT("common:close", "Close")}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      {safeT("profile.edit.title", "Edit Profile")}
                    </h2>
                    <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 truncate">
                      {safeT("profile.edit.subtitle", "Update your personal information and preferences")}
                    </p>
                  </div>
                </div>

              </div>

              <div className="overflow-y-auto p-3 sm:p-4">
                <ProfileEditForm
                  profile={profile}
                  updateProfile={updateProfile}
                  signedIn={signedIn}
                  currentUser={currentUser}
                  onSubmitSuccess={handleEditSubmitSuccess}
                  onCancel={closeEditModal}
                  showCancel
                  compact
                  initialSection={editInitialSection}
                  saveLabel={safeT("profile.edit.buttons.save", "Save")}
                  savingLabel={safeT("profile.edit.buttons.saving", "Saving...")}
                />
              </div>
            </div>
          </div>
        )}
    </ProfilePageShell>
  );
}
